import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, Cpu, MapPin, Navigation, UserPlus, X } from 'lucide-react'
import { toast } from 'sonner'

import { SearchableSelect } from '@/components/common/searchable-select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { withServiceInstanceId } from '@/lib/service-instance'
import { cn } from '@/lib/utils'
import {
  formatTimeForLocale,
  getHourFractionInTimeZone,
  parseApiDateTimeValue,
  useOrganizationDateLocale,
} from '@/lib/org-date-locale'
import {
  attendanceBoardApi,
  type AttendanceBoardLocation,
  type AttendanceBoardSegment,
  type AttendanceBoardWorkerGroup,
  type AttendanceBoardRosterWorker,
} from '@/services/attendance-board'
import { workersApi } from '@/services/workers'
import { getErrorMessage } from '@/lib/api'
import { WorkerProfileLink } from '@/pages/attendance/worker-link'
import { parseServerDate, useTenantTimeZone } from '@/lib/tenant-date-time'
import { useAuthStore } from '@/stores/auth-store'

function todayIsoDate() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// function formatTime(value: string | null | undefined, timeZone?: string, locale = 'en-GB') {
//   if (!value) return '—'
//   return formatTimeForLocale(value, locale, '—', timeZone)
// }

function formatOrgSegmentTime(
  utcValue: string | null | undefined,
  localValue: string | null | undefined,
  timeZone?: string,
  locale = 'en-GB',
) {
  const preferred = String(localValue || '').trim() || utcValue
  if (!preferred) return '—'
  const parsed = parseApiDateTimeValue(preferred)
  if (!parsed) return preferred
  return formatTimeForLocale(parsed, locale, '—', timeZone)
}

function orgDateIso(timeZone?: string) {
  if (!timeZone) return todayIsoDate()
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date())
}

function formatDateTime(value: string | null | undefined, timeZone?: string | null) {
  if (!value) return '—'
  const parsed = timeZone ? parseServerDate(value) : new Date(value)
  if (!parsed || Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })
}

function formatCoordinate(value: string | number | null | undefined) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return String(value)
  return numeric.toFixed(6)
}

const HOUR_SLOT_STARTS = Array.from({ length: 12 }, (_, index) => index * 2)
const MINUTES_PER_DAY = 24 * 60

function workerGroupKey(group: AttendanceBoardWorkerGroup) {
  return group.worker_id != null ? String(group.worker_id) : `name:${group.worker_name}`
}

function isExpectedGroup(group: AttendanceBoardWorkerGroup) {
  return group.segments.every((segment) => String(segment.status).toLowerCase() === 'expected')
}

function getWorkerFilterKey(worker: { id: string | number; worker_id?: string | number }) {
  const raw = worker.worker_id ?? worker.id
  return String(raw)
}

function parseToMinutesSinceMidnight(value: string | null | undefined, timeZone?: string): number | null {
  const parsed = parseApiDateTimeValue(value)
  if (!parsed) return null
  return getHourFractionInTimeZone(parsed, timeZone) * 60
}

function resolveNowMinutes(selectedDate: string, timeZone?: string) {
  const today = orgDateIso(timeZone)
  if (selectedDate !== today) {
    return MINUTES_PER_DAY
  }
  return getHourFractionInTimeZone(new Date(), timeZone) * 60
}

function formatSegmentTooltip(
  segment: AttendanceBoardSegment,
  index: number,
  timeZone?: string,
  locale = 'en-GB',
) {
  const isOpen = String(segment.status).toLowerCase() === 'open'
  const checkIn = formatOrgSegmentTime(segment.check_in, segment.check_in_local, timeZone, locale)
  const checkOut = isOpen
    ? 'On site'
    : formatOrgSegmentTime(segment.check_out, segment.check_out_local, timeZone, locale)
  return {
    label: `Session ${segment.segment_index ?? index + 1}`,
    checkIn,
    checkOut,
    hours: segment.hours && segment.hours !== '0.00' ? `${segment.hours}h` : null,
  }
}

function AttendanceTimelineBar({
  segments,
  selectedDate,
  orgTimezone,
  locale,
}: {
  segments: AttendanceBoardSegment[]
  selectedDate: string
  orgTimezone?: string | null
  locale: string
}) {
  const timeZone = orgTimezone || undefined
  const nowMinutes = resolveNowMinutes(selectedDate, timeZone)

  const segmentBars = segments.flatMap((segment, index) => {
    const startSource = segment.check_in_local || segment.check_in
    const endSource = segment.check_out_local || segment.check_out
    const startMin = parseToMinutesSinceMidnight(startSource, timeZone)
    if (startMin == null) return []
    const isOpen = String(segment.status).toLowerCase() === 'open'
    const checkoutMin = parseToMinutesSinceMidnight(endSource, timeZone)
    const endMin = isOpen ? Math.min(nowMinutes, MINUTES_PER_DAY) : checkoutMin ?? startMin
    if (endMin <= startMin) return []

    return [{
      key: `${segment.attendance_id ?? segment.entry_id}-${index}`,
      left: (startMin / MINUTES_PER_DAY) * 100,
      width: ((endMin - startMin) / MINUTES_PER_DAY) * 100,
      isOpen,
      segment,
      index,
    }]
  })

  return (
    <div className="relative h-9">
      <div className="grid h-full grid-cols-12 gap-0.5">
        {HOUR_SLOT_STARTS.map((hour) => (
          <div
            key={hour}
            className="h-full rounded-sm border border-border/40 bg-muted/25"
            aria-hidden="true"
          />
        ))}
      </div>
      <div className="pointer-events-none absolute inset-0">
        {segmentBars.map((bar) => {
          const tooltip = formatSegmentTooltip(bar.segment, bar.index, timeZone, locale)
          return (
            <Tooltip key={bar.key}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'pointer-events-auto absolute inset-y-0 rounded-sm border-0 p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60',
                    bar.isOpen ? 'bg-emerald-600 hover:bg-emerald-600/90' : 'bg-emerald-500/55 hover:bg-emerald-500/70',
                  )}
                  style={{ left: `${bar.left}%`, width: `${Math.max(bar.width, 0.8)}%` }}
                  onClick={(event) => event.stopPropagation()}
                  aria-label={`${tooltip.label}: in ${tooltip.checkIn}, out ${tooltip.checkOut}`}
                />
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={6} className="space-y-1 px-3 py-2">
                <p className="font-medium">{tooltip.label}</p>
                <p>In: {tooltip.checkIn}</p>
                <p>Out: {tooltip.checkOut}</p>
                {tooltip.hours ? <p className="text-background/80">{tooltip.hours}</p> : null}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}

function WorkerSessionDetails({
  group,
  orgTimezone,
  locale,
}: {
  group: AttendanceBoardWorkerGroup
  orgTimezone?: string | null
  locale: string
}) {
  const timeZone = orgTimezone || undefined
  return (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">
          <WorkerProfileLink workerId={group.worker_id} name={group.worker_name} /> — session details
        </p>
        <span className="text-xs text-muted-foreground">{group.total_hours}h total today</span>
      </div>
      <div className="overflow-x-auto rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Segment</TableHead>
              <TableHead>Check in</TableHead>
              <TableHead>Check out</TableHead>
              <TableHead className="w-[80px]">Hours</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.segments.map((segment, index) => {
              const isOpen = String(segment.status).toLowerCase() === 'open'
              return (
                <TableRow key={`${segment.attendance_id ?? segment.entry_id}-${index}`}>
                  <TableCell>{segment.segment_index ?? index + 1}</TableCell>
                  <TableCell>{formatOrgSegmentTime(segment.check_in, segment.check_in_local, timeZone, locale)}</TableCell>
                  <TableCell>{formatOrgSegmentTime(segment.check_out, segment.check_out_local, timeZone, locale)}</TableCell>
                  <TableCell>{segment.hours || '0.00'}</TableCell>
                  <TableCell>
                    {isOpen ? (
                      <Badge className="bg-emerald-600 hover:bg-emerald-600">Open</Badge>
                    ) : (
                      <Badge variant="secondary">Closed</Badge>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function WorkerAttendanceRow({
  group,
  selectedDate,
  orgTimezone,
  locale,
  expanded,
  onToggle,
}: {
  group: AttendanceBoardWorkerGroup
  selectedDate: string
  orgTimezone?: string | null
  locale: string
  expanded: boolean
  onToggle: () => void
}) {
  const segments = group.segments
  if (segments.length === 0) {
    return null
  }

  const hasOpenSession = segments.some((segment) => String(segment.status).toLowerCase() === 'open')
  const expectedOnly = isExpectedGroup(group)
  const rowKey = workerGroupKey(group)

  return (
    <>
      <TableRow
        data-state={expanded ? 'open' : 'closed'}
        className={cn(
          'cursor-pointer transition-colors hover:bg-muted/40',
          hasOpenSession && 'bg-emerald-50/60 dark:bg-emerald-950/20',
          expectedOnly && 'bg-muted/20',
          expanded && 'bg-muted/30',
        )}
        onClick={expectedOnly ? undefined : onToggle}
        aria-expanded={expectedOnly ? undefined : expanded}
      >
        <TableCell className="align-middle font-medium">
          <div className="flex flex-col gap-1">
            <WorkerProfileLink workerId={group.worker_id} name={group.worker_name} className="font-medium" />
            {group.also_on_other_jobs ? (
              <span className="text-xs font-normal text-muted-foreground">Also on other jobs</span>
            ) : null}
            {!expectedOnly ? (
              <span className="text-[11px] font-normal text-muted-foreground">
                {expanded ? 'Hide details' : 'Click for punch times'}
              </span>
            ) : (
              <span className="text-[11px] font-normal text-muted-foreground">
                {group.on_roster ? 'Preloaded — awaiting punch' : 'Awaiting punch'}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell className="align-middle">
          {expectedOnly ? (
            <div className="flex h-9 items-center rounded-md border border-dashed bg-muted/10 px-3 text-xs text-muted-foreground">
              No punch yet
            </div>
          ) : (
            <AttendanceTimelineBar
              segments={segments}
              selectedDate={selectedDate}
              orgTimezone={orgTimezone}
              locale={locale}
            />
          )}
        </TableCell>
        <TableCell className="align-middle">{expectedOnly ? '—' : `${group.total_hours}h`}</TableCell>
        <TableCell className="align-middle">
          {expectedOnly ? (
            <Badge variant="outline">Expected</Badge>
          ) : hasOpenSession ? (
            <Badge className="bg-emerald-600 hover:bg-emerald-600">On site</Badge>
          ) : (
            <Badge variant="secondary">Off site</Badge>
          )}
        </TableCell>
      </TableRow>
      {expanded && !expectedOnly ? (
        <TableRow key={`${rowKey}-details`} className="hover:bg-transparent">
          <TableCell colSpan={4} className="bg-muted/10 p-4">
            <WorkerSessionDetails group={group} orgTimezone={orgTimezone} locale={locale} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
      <div className="text-sm font-medium text-foreground break-words">{value || '—'}</div>
    </div>
  )
}

function SelectedLocationDetails({
  location,
  serviceInstanceId,
}: {
  location: AttendanceBoardLocation
  serviceInstanceId?: string | null
}) {
  const latitude = formatCoordinate(location.latitude)
  const longitude = formatCoordinate(location.longitude)
  const coordinates = latitude && longitude
    ? `${latitude}, ${longitude}`
    : location.center_latlog || null
  const device = location.device
  // Device "Last seen" only; the board's segment times keep the backend org_timezone.
  const tenantTimeZone = useTenantTimeZone(serviceInstanceId)
  const locationPath = withServiceInstanceId(`/job/locations?location_id=${location.id}`, serviceInstanceId)

  return (
    <Card className="border-border/80">
      <CardContent className="grid gap-5 p-4 md:grid-cols-2">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold tracking-tight">{location.place}</h2>
                <Badge variant="outline">#{location.id}</Badge>
                {location.active === false ? (
                  <Badge variant="secondary">Inactive</Badge>
                ) : (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">Active</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Selected location details</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <DetailItem label="Place" value={location.place} />
            <DetailItem
              label="Coordinates"
              value={
                coordinates ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Navigation className="h-3.5 w-3.5 text-muted-foreground" />
                    {coordinates}
                  </span>
                ) : (
                  'Not set'
                )
              }
            />
            <DetailItem
              label="Device link"
              value={location.has_device ? 'Attendance device attached' : 'No device attached'}
            />
            <DetailItem
              label="Manage"
              value={
                <Link
                  to={locationPath}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Open in Locations
                </Link>
              }
            />
          </div>
        </div>

        <div className="space-y-4 rounded-lg border border-dashed bg-muted/20 p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md border bg-background p-2">
              <Cpu className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 space-y-1">
              <h3 className="text-sm font-semibold">Attendance device</h3>
              <p className="text-xs text-muted-foreground">
                {device ? 'Device linked to this location' : 'No ADMS device is linked yet'}
              </p>
            </div>
          </div>

          {device ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <DetailItem label="Name" value={device.name || device.device_sn} />
              <DetailItem label="Serial number" value={device.device_sn} />
              <DetailItem
                label="Mode"
                value={device.attendance_mode_label || device.attendance_mode || '—'}
              />
              <DetailItem
                label="Status"
                value={
                  device.active === false ? (
                    <Badge variant="secondary">Inactive</Badge>
                  ) : (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600">Active</Badge>
                  )
                }
              />
              <DetailItem label="Last seen" value={formatDateTime(device.last_seen_at, tenantTimeZone)} />
              <DetailItem label="Device ID" value={`#${device.id}`} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Link an ADMS device on the Locations page to capture punches for this site.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function AttendanceRosterPanel({
  locationId,
  serviceInstanceId,
  rosterWorkers,
  selectedDate,
}: {
  locationId: number
  serviceInstanceId?: string | null
  rosterWorkers: AttendanceBoardRosterWorker[]
  selectedDate: string
}) {
  const queryClient = useQueryClient()
  const [selectedWorkerId, setSelectedWorkerId] = useState('')
  const [allocationType, setAllocationType] = useState('daily')
  const [startDate, setStartDate] = useState(selectedDate)
  const [endDate, setEndDate] = useState(selectedDate)

  useEffect(() => {
    setStartDate(selectedDate)
    setEndDate(selectedDate)
    setSelectedWorkerId('')
    setAllocationType('daily')
  }, [selectedDate, locationId])

  const workersQuery = useQuery({
    queryKey: ['workers', 'attendance-roster', serviceInstanceId],
    queryFn: () => workersApi.getWorkers({ page_size: 300, service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const workerSelectOptions = useMemo(() => {
    const workers = workersQuery.data?.results ?? []
    return workers
      .map((worker) => ({
        value: getWorkerFilterKey(worker),
        label: worker.display_name || worker.full_name || `Worker ${worker.id}`,
        keywords: `${worker.display_name || ''} ${worker.full_name || ''} ${worker.email || ''}`.trim(),
      }))
  }, [workersQuery.data?.results])

  const invalidateBoard = async () => {
    await queryClient.invalidateQueries({ queryKey: ['attendance-board-day'] })
    await queryClient.invalidateQueries({ queryKey: ['attendance-dashboard-roster'] })
    await queryClient.invalidateQueries({ queryKey: ['job-locations'] })
  }

  const addMutation = useMutation({
    mutationFn: (workerId: number) => {
      if (allocationType === 'permanent') {
        return attendanceBoardApi.addRosterWorker(locationId, workerId, null, null)
      }
      if (allocationType === 'daily') {
        return attendanceBoardApi.addRosterWorker(locationId, workerId, selectedDate, selectedDate)
      }
      if (!startDate || !endDate || endDate < startDate) {
        throw new Error('Choose a valid start and end date.')
      }
      return attendanceBoardApi.addRosterWorker(locationId, workerId, startDate, endDate)
    },
    onSuccess: async () => {
      setSelectedWorkerId('')
      await invalidateBoard()
      toast.success('Worker added to roster')
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Could not add worker to roster')),
  })

  const removeMutation = useMutation({
    mutationFn: (worker: AttendanceBoardRosterWorker) => attendanceBoardApi.removeRosterWorker(locationId, worker.worker_id, worker.allocation_id),
    onSuccess: async () => {
      await invalidateBoard()
      toast.success('Worker removed from roster')
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Could not remove worker from roster')),
  })

  return (
    <Card className="border-border/80">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Daily roster</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="min-w-0 flex-1 space-y-2">
            <Label htmlFor="attendance-roster-worker">Add worker</Label>
            <SearchableSelect
              ariaLabel="Add worker to attendance roster"
              value={selectedWorkerId}
              onValueChange={setSelectedWorkerId}
              options={workerSelectOptions}
              placeholder="Select worker"
              searchPlaceholder="Search workers"
              emptyText={workersQuery.isLoading ? 'Loading workers…' : 'No workers available.'}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="roster-allocation-type">Assignment</Label>
            <Select value={allocationType} onValueChange={setAllocationType}>
              <SelectTrigger id="roster-allocation-type" className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Selected day only</SelectItem>
                <SelectItem value="range">Date range</SelectItem>
                <SelectItem value="permanent">Permanent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {allocationType === 'range' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="roster-start-date">Start date</Label>
                <Input id="roster-start-date" type="date" value={startDate} max={endDate || undefined} onChange={(event) => setStartDate(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="roster-end-date">End date</Label>
                <Input id="roster-end-date" type="date" value={endDate} min={startDate || undefined} onChange={(event) => setEndDate(event.target.value)} />
              </div>
            </>
          ) : null}
          <Button
            type="button"
            className="gap-1.5"
            disabled={!selectedWorkerId || (allocationType === 'range' && (!startDate || !endDate || endDate < startDate)) || addMutation.isPending}
            onClick={() => addMutation.mutate(Number(selectedWorkerId))}
          >
            <UserPlus className="h-4 w-4" />
            Add to roster
          </Button>
        </div>

        {rosterWorkers.length === 0 ? (
          <p className="rounded-md border border-dashed px-4 py-6 text-sm text-muted-foreground">
            No workers allocated for this date.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {rosterWorkers.map((worker) => (
              <Badge key={worker.allocation_id ?? worker.worker_id} variant="secondary" className="gap-1.5 py-1.5 pl-3 pr-1.5">
                <WorkerProfileLink workerId={worker.worker_id} name={worker.worker_name} className="font-medium" />
                {worker.start_date || worker.end_date ? (
                  <span className="text-xs">{worker.start_date ?? 'Any date'} - {worker.end_date ?? 'Any date'}</span>
                ) : <span className="text-xs">Permanent</span>}
                <button
                  type="button"
                  className="rounded-sm p-0.5 hover:bg-background/60"
                  aria-label={`Remove ${worker.worker_name} from roster`}
                  disabled={removeMutation.isPending}
                  onClick={() => removeMutation.mutate(worker)}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </Badge>
            ))}
          </div>
        )}

      </CardContent>
    </Card>
  )
}

export default function AttendanceBoardPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const serviceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const [selectedDate, setSelectedDate] = useState(() => searchParams.get('date') || todayIsoDate())
  const [expandedWorkerKey, setExpandedWorkerKey] = useState<string | null>(null)

  const locationsQuery = useQuery({
    queryKey: ['attendance-board-locations', serviceInstanceId],
    queryFn: () => attendanceBoardApi.listLocations(),
  })

  const locations = locationsQuery.data ?? []
  const locationFromQuery = Number(searchParams.get('location_id') || 0)
  const selectedLocationId = useMemo(() => {
    if (locationFromQuery && locations.some((row) => row.id === locationFromQuery)) {
      return locationFromQuery
    }
    return locations[0]?.id ?? null
  }, [locationFromQuery, locations])

  const selectedLocation = useMemo(
    () => locations.find((row) => row.id === selectedLocationId) ?? null,
    [locations, selectedLocationId],
  )

  useEffect(() => {
    setExpandedWorkerKey(null)
  }, [selectedDate, selectedLocationId])

  useEffect(() => {
    if (!selectedLocationId) return
    const next = new URLSearchParams(searchParams)
    let changed = false
    if (String(selectedLocationId) !== next.get('location_id')) {
      next.set('location_id', String(selectedLocationId))
      changed = true
    }
    if (selectedDate !== next.get('date')) {
      next.set('date', selectedDate)
      changed = true
    }
    if (changed) {
      setSearchParams(next, { replace: true })
    }
  }, [selectedDate, selectedLocationId, searchParams, setSearchParams])

  const showBranchWorkers = !locationsQuery.isLoading && locations.length === 0
  const dayQuery = useQuery({
    queryKey: ['attendance-board-day', serviceInstanceId, selectedLocationId, selectedDate],
    queryFn: () => attendanceBoardApi.getDay(selectedLocationId, selectedDate),
    enabled: Boolean(selectedLocationId) || showBranchWorkers,
  })

  const workerGroups = dayQuery.data?.worker_groups ?? []
  const rosterWorkers = dayQuery.data?.roster ?? []
  const orgTimezone = dayQuery.data?.org_timezone ?? null
  const dateLocale = useOrganizationDateLocale(serviceInstanceId)
  const dayLocation = dayQuery.data?.location
  const detailsLocation = dayLocation?.id === selectedLocationId
    ? { ...selectedLocation, ...dayLocation } as AttendanceBoardLocation
    : selectedLocation

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Attendance</h1>
          <p className="text-sm text-muted-foreground">
            24-hour timeline in 2-hour blocks (organisation timezone). Click a worker row for punch times.
          </p>
          {orgTimezone ? (
            <p className="text-xs text-muted-foreground">Times shown in {orgTimezone}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <Input
            type="date"
            className="w-[170px]"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value || todayIsoDate())}
          />
        </div>
      </div>

      {locationsQuery.isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : locations.length === 0 ? (
        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          No locations yet, so all workers of this branch are shown. Add locations under Jobs → Locations to see attendance per site.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Attendance locations">
            {locations.map((location) => {
              const active = location.id === selectedLocationId
              return (
                <Button
                  key={location.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  variant={active ? 'default' : 'outline'}
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams)
                    next.set('location_id', String(location.id))
                    next.set('date', selectedDate)
                    setSearchParams(next)
                  }}
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {location.place}
                  {location.has_device ? (
                    <span className="ml-0.5 text-[10px] opacity-80">· device</span>
                  ) : null}
                </Button>
              )
            })}
          </div>

          {detailsLocation ? (
            <SelectedLocationDetails
              location={detailsLocation}
              serviceInstanceId={serviceInstanceId}
            />
          ) : null}
        </div>
      )}

      {selectedLocationId || showBranchWorkers ? (
        <div className="space-y-3">
          {selectedLocationId ? (
            <AttendanceRosterPanel
              locationId={selectedLocationId}
              serviceInstanceId={serviceInstanceId}
              rosterWorkers={rosterWorkers}
              selectedDate={selectedDate}
            />
          ) : null}

          {dayQuery.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : dayQuery.isError ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-8 text-center text-sm text-destructive">
              <p>Could not load attendance for {selectedDate}.</p>
              <p className="mt-2 text-xs text-destructive/90">
                {getErrorMessage(dayQuery.error, 'Refresh or check your session / permissions.')}
              </p>
            </div>
          ) : workerGroups.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              {selectedLocationId
                ? `No workers on the roster or punched at this location on ${selectedDate}.`
                : 'No approved workers in this branch yet.'}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[160px]">Worker</TableHead>
                    <TableHead>
                      <div className="space-y-1">
                        <span className="text-xs font-normal text-muted-foreground">Day timeline (2h blocks)</span>
                        <div className="grid grid-cols-12 gap-0.5 text-[10px] font-normal text-muted-foreground">
                          {HOUR_SLOT_STARTS.map((hour) => (
                            <span key={hour} className="text-center tabular-nums">
                              {hour}
                            </span>
                          ))}
                        </div>
                      </div>
                    </TableHead>
                    <TableHead className="w-[72px]">Total</TableHead>
                    <TableHead className="w-[96px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workerGroups.map((group) => {
                    const key = workerGroupKey(group)
                    return (
                      <WorkerAttendanceRow
                        key={key}
                        group={group}
                        selectedDate={selectedDate}
                        orgTimezone={orgTimezone}
                        locale={dateLocale}
                        expanded={expandedWorkerKey === key}
                        onToggle={() => setExpandedWorkerKey((current) => (current === key ? null : key))}
                      />
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
